// Backend · Alerts: the editor for a notification channel (webhook or email).

import { useId, useState } from 'react';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { MutationErrorLine } from '@/components/error-panel';
import { Icons } from '@/components/primitives';
import { adminNotificationChannels } from '@/api/resources';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import type { AlertRule, ChannelKind, NotificationChannel, NotificationChannelUpsert } from '@/api/types';
import {
  CHANNEL_NAME_MAX,
  CHANNEL_RECIPIENTS_MAX,
  EMAIL_PATTERN,
  RECIPIENT_ADDRESS_MAX,
  isValidWebhookUrl,
} from '@/lib/alert-validation';
import { DeleteWithConfirm, Field, channelUsageSentence, invalidate } from './backend-alerts-parts';

export interface ChannelDrawerProps {
  channel: NotificationChannel | null;
  /**
   * The rules on the screen, to name the ones that deliver through this
   * channel when it is deleted. Omit it while they are not loaded.
   */
  rules?: readonly Pick<AlertRule, 'name' | 'channel_ids'>[];
  onClose: () => void;
}

export function ChannelDrawer({ channel, rules, onClose }: ChannelDrawerProps) {
  const editing = !!channel;
  const fieldIdBase = useId();
  const fid = (name: string) => `${fieldIdBase}-${name}`;
  const {
    messages: channelRequiredMessages,
    attempt: channelRequiredAttempt,
    report: reportChannelRequired,
    clear: clearChannelRequired,
  } = useRequiredFeedback();
  const [name, setName] = useState(channel?.name ?? '');
  const [kind, setKind] = useState<ChannelKind>(channel?.kind ?? 'webhook');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [recipients, setRecipients] = useState((channel?.recipients ?? []).join(', '));

  const saveM = useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: (body: NotificationChannelUpsert) => channel
      ? api.patch(`/admin/notification-channels/${encodeURIComponent(channel.id)}`, { body })
      : api.post('/admin/notification-channels', { body }),
    onSuccess: () => { invalidate(adminNotificationChannels.all); onClose(); },
  });
  const deleteM = useRegisteredMutation({
    domains: ['configuration'],
    mutationFn: async (_: void) => {
      // Delete is offered only while editing a saved channel.
      if (!channel) throw new Error('There is no saved channel to delete.');
      return api.delete(`/admin/notification-channels/${encodeURIComponent(channel.id)}`);
    },
    onSuccess: () => { invalidate(adminNotificationChannels.all); onClose(); },
  });

  const recipientList = recipients.split(',').map((recipient) => recipient.trim()).filter(Boolean);
  const keepsExistingWebhook = editing
    && channel.kind === 'webhook'
    && channel.configured
    && kind === 'webhook';
  const webhookValue = webhookUrl.trim();
  const invalidRecipients = recipientList.filter((recipient) => !EMAIL_PATTERN.test(recipient));
  const destinationError = kind === 'webhook'
    ? !webhookValue && !keepsExistingWebhook
      ? 'Enter the webhook URL used to deliver alerts.'
      : webhookValue && !isValidWebhookUrl(webhookValue)
        ? 'Enter a valid Slack or Discord HTTPS webhook URL.'
        : null
    : recipientList.length === 0
      ? 'Enter at least one email recipient.'
      : invalidRecipients.length > 0
        ? `Enter valid email addresses${invalidRecipients.length === 1 ? ` — ${invalidRecipients[0]} is invalid.` : '.'}`
        : null;
  // Case-insensitive, because the server lowercases before its uniqueness check.
  const duplicateRecipients = recipientList.length
    !== new Set(recipientList.map((address) => address.trim().toLowerCase())).size;
  const overlongRecipient = recipientList.find(
    (address) => address.trim().length > RECIPIENT_ADDRESS_MAX,
  );
  const channelIssues = [
    !name.trim() && { key: 'notification-channel-name', message: 'Enter a channel name.' },
    name.trim().length > CHANNEL_NAME_MAX && {
      key: 'notification-channel-name',
      message: `Channel name must be ${CHANNEL_NAME_MAX} characters or fewer.`,
    },
    destinationError ? { key: 'notification-channel-destination', message: destinationError } : null,
    kind === 'email' && recipientList.length > CHANNEL_RECIPIENTS_MAX && {
      key: 'notification-channel-destination',
      message: `Up to ${CHANNEL_RECIPIENTS_MAX} recipients per channel.`,
    },
    kind === 'email' && duplicateRecipients && {
      key: 'notification-channel-destination',
      message: 'Remove duplicate recipients — addresses are matched case-insensitively.',
    },
    kind === 'email' && overlongRecipient ? {
      key: 'notification-channel-destination',
      message: `${overlongRecipient.trim().slice(0, 40)}… is too long to be an email address.`,
    } : null,
  ].filter(Boolean);

  const body = (): NotificationChannelUpsert => ({
    name: name.trim(), kind,
    webhook_url: kind === 'webhook' ? (webhookValue || null) : null,
    recipients: kind === 'email' ? recipientList : [],
  });
  const attemptSaveChannel = () => {
    if (saveM.isPending) return;
    if (!reportChannelRequired(channelIssues)) return;
    saveM.mutate(body());
  };

  return (
    <>

      <ResponsiveOverlay label="backend alerts details" onClose={onClose} className="drawer" style={{ width: 460 }}>
        <div className="drawer-hd">
          <RequiredField
            fieldKey="notification-channel-name"
            message={channelRequiredMessages['notification-channel-name']}
            attempt={channelRequiredAttempt}
            style={{ flex: 1 }}
          >
            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase' }}>{editing ? 'Edit channel' : 'New channel'}</div>
            <input
              className="input"
              data-validation-focus
              style={{ fontSize: 16, fontWeight: 700, marginTop: 4, width: '100%' }}
              aria-label="Channel name"
              placeholder="Channel name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearChannelRequired('notification-channel-name');
              }}
            />
          </RequiredField>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ fontSize: 18, padding: '4px 10px' }}>×</button>
        </div>
        <div className="drawer-body">
          <label className="field-lbl" htmlFor={fid('kind')}><span>Kind</span></label>
          <select id={fid('kind')} className="input" style={{ width: '100%', fontSize: 13, marginBottom: 12 }} value={kind} onChange={(e) => {
            // The two options below are the only values this select can hold.
            const next = e.target.value;
            if (next === 'webhook' || next === 'email') setKind(next);
            clearChannelRequired('notification-channel-destination');
          }}>
            <option value="webhook">Webhook (Slack / Discord)</option>
            <option value="email">Email</option>
          </select>
          {kind === 'webhook' ? (
            <Field controlId={fid('webhook-url')}
              label={keepsExistingWebhook ? 'Webhook URL (leave blank to keep)' : 'Webhook URL'}
              fieldKey="notification-channel-destination"
              message={channelRequiredMessages['notification-channel-destination']}
              attempt={channelRequiredAttempt}
            >
              <input id={fid('webhook-url')} className="input mono" style={{ width: '100%', fontSize: 12 }} placeholder="https://hooks.slack.com/…" value={webhookUrl} onChange={(e) => {
                setWebhookUrl(e.target.value);
                clearChannelRequired('notification-channel-destination');
              }} />
            </Field>
          ) : (
            <Field controlId={fid('recipients')}
              label="Recipients (comma-separated)"
              fieldKey="notification-channel-destination"
              message={channelRequiredMessages['notification-channel-destination']}
              attempt={channelRequiredAttempt}
            >
              <input id={fid('recipients')} className="input mono" style={{ width: '100%', fontSize: 12 }} placeholder="oncall@shubertads.com, ops@…" value={recipients} onChange={(e) => {
                setRecipients(e.target.value);
                clearChannelRequired('notification-channel-destination');
              }} />
            </Field>
          )}
          {saveM.error && <div style={{ marginTop: 8 }}><MutationErrorLine error={saveM.error} /></div>}
        </div>
        {deleteM.error ? <div style={{ padding: '8px 16px 0' }}><MutationErrorLine error={deleteM.error} /></div> : null}
        <div className="drawer-foot" style={{ position: 'relative' }}>
          {channel && (
            <DeleteWithConfirm
              title="Delete this channel?"
              body={['It can’t be restored.', channelUsageSentence(channel.id, rules)].filter(Boolean).join(' ')}
              busy={deleteM.isPending}
              onConfirm={() => deleteM.mutate()}
            />
          )}
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} disabled={saveM.isPending} onClick={attemptSaveChannel}>
            {Icons.check} {saveM.isPending ? 'Saving…' : 'Save channel'}
          </button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
